import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import {
  A2P_BUSINESS_INDUSTRIES,
  A2P_BUSINESS_TYPES,
  A2P_COMPANY_TYPES,
  A2P_REGISTRATION_IDENTIFIERS,
  A2pAuthorizedRepSchema,
  A2pBusinessAddressSchema
} from "../../../../schemas/ApiKeyConnection/settings/A2pRegistration.schema";
import { ComplianceFormSchema } from "../../../../schemas/ApiKeyConnection/settings/ComplianceForm.schema";
import { SupportedTwilioRegionSchema } from "../twilio-region.types";
import { ComplianceBundleResponseSchema } from "./whatsapp.schema";

// ============================================================================
// REGULATION REQUIREMENTS (Twilio Regulations API)
// ============================================================================

/**
 * A single field required by a regulation (end-user or document attribute).
 *
 * Maps to Twilio's `detailed_fields[]` inside `requirements.end_user[]`
 * and `requirements.supporting_document[][].accepted_documents[]`.
 */
export const RegulationFieldSchema = z.object({
  machineName: z.string(),
  friendlyName: z.string(),
  constraint: z.string(),
  description: z.string().optional()
});

export type RegulationField = z.infer<typeof RegulationFieldSchema>;

/**
 * An end-user requirement — the set of fields needed to describe the end user.
 */
export const RegulationEndUserRequirementSchema = z.object({
  name: z.string(),
  type: z.string(),
  fields: z.array(RegulationFieldSchema)
});

export type RegulationEndUserRequirement = z.infer<typeof RegulationEndUserRequirementSchema>;

/**
 * A document type that Twilio accepts for a supporting-document requirement.
 */
export const RegulationAcceptedDocumentSchema = z.object({
  name: z.string(),
  type: z.string(),
  fields: z.array(RegulationFieldSchema)
});

export type RegulationAcceptedDocument = z.infer<typeof RegulationAcceptedDocumentSchema>;

/**
 * A single AND requirement the user must satisfy (e.g. "Proof of Identity").
 *
 * Twilio's regulations expose multiple AND requirements per regulation —
 * for BE-business: Proof of Address, Proof of Business Registration Number,
 * Proof of Identity. Each requirement has its own accepted_documents (any of
 * which satisfies that one requirement) and its own detailed_fields. The
 * user picks one document type per requirement, fills its fields, and
 * uploads a file. The backend then dedupes by document type — Twilio rejects
 * bundles with duplicate types — and creates a single SupportingDocument per
 * unique type carrying the union of fields needed across the requirements
 * that selected it.
 *
 * `requirementName` — Twilio's stable identifier (e.g. "business_name_info").
 * Used as a refetch-stable key for per-requirement state on the frontend.
 *
 * `requiresAddress` — when true, the document needs a Twilio Address SID.
 * The frontend shows the AddressPicker and the backend attaches `address_sids`
 * to the supporting document attributes on submit.
 */
export const RegulationDocumentRequirementSchema = z.object({
  requirementName: z.string().min(1),
  name: z.string(),
  description: z.string().optional(),
  requiresAddress: z.boolean().default(false),
  acceptedDocuments: z.array(RegulationAcceptedDocumentSchema)
});

export type RegulationDocumentRequirement = z.infer<typeof RegulationDocumentRequirementSchema>;

/**
 * Full regulation requirements response — frontend-friendly shape.
 */
export const RegulationRequirementsResponseSchema = z.object({
  regulationSid: z.string(),
  friendlyName: z.string(),
  isoCountry: z.string(),
  numberType: z.string(),
  endUserType: z.string(),
  endUserRequirements: z.array(RegulationEndUserRequirementSchema),
  documentRequirements: z.array(RegulationDocumentRequirementSchema)
});

export type RegulationRequirementsResponse = z.infer<typeof RegulationRequirementsResponseSchema>;

/**
 * Get Available Regulation Types Query/Response
 *
 * Returns which number types (local, mobile, toll_free) have regulations
 * for a given country. Used by the compliance wizard to filter type cards.
 */
export const GetAvailableRegulationTypesQuerySchema = z.object({
  country: z.string().min(2).max(2),
  endUserType: z.enum(["individual", "business"])
});

export type GetAvailableRegulationTypesQuery = z.infer<
  typeof GetAvailableRegulationTypesQuerySchema
>;

export const GetAvailableRegulationTypesResponseSchema = z.array(z.string());

export type GetAvailableRegulationTypesResponse = z.infer<
  typeof GetAvailableRegulationTypesResponseSchema
>;

export const GetRegulationRequirementsQuerySchema = z.object({
  country: z.string().min(2).max(2),
  numberType: z.string(),
  endUserType: z.enum(["individual", "business"])
});

export type GetRegulationRequirementsQuery = z.infer<typeof GetRegulationRequirementsQuerySchema>;

// ============================================================================
// PHONE NUMBER INFO (shared response type)
// ============================================================================

/**
 * Phone Number Info Schema
 *
 * Represents a phone number available for purchase from Twilio.
 * Matches the PhoneNumberInfo interface in deployment.types.ts
 */
export const PhoneNumberInfoSchema = z.object({
  friendlyName: z.string(),
  phoneNumber: z.string(),
  current_price: z.string(),
  currency: z.string()
});

export type PhoneNumberInfoResponse = z.infer<typeof PhoneNumberInfoSchema>;

// ============================================================================
// GET AVAILABLE PHONE NUMBER TYPES (per country)
// ============================================================================

/**
 * Get Available Phone Number Types Query Schema
 *
 * Probes Twilio to check which number types (local, mobile, tollFree)
 * are available for a given country.
 */
export const GetAvailablePhoneNumberTypesQuerySchema = z.object({
  country: z.string().min(2).max(2)
});

export type GetAvailablePhoneNumberTypesQuery = z.infer<
  typeof GetAvailablePhoneNumberTypesQuerySchema
>;

export const GetAvailablePhoneNumberTypesResponseSchema = z.object({
  local: z.boolean(),
  mobile: z.boolean(),
  tollFree: z.boolean()
});

export type GetAvailablePhoneNumberTypesResponse = z.infer<
  typeof GetAvailablePhoneNumberTypesResponseSchema
>;

// ============================================================================
// SAVE COMPLIANCE DRAFT
// ============================================================================

export const SaveComplianceDraftBodySchema = z.object({
  connectionId: z.string().uuid(),
  formData: ComplianceFormSchema.partial(),
  step: z.number().int().min(0).max(3)
});

export type SaveComplianceDraftBody = z.infer<typeof SaveComplianceDraftBodySchema>;

export const SaveComplianceDraftResponseSchema = ComplianceBundleResponseSchema;

export type SaveComplianceDraftResponse = z.infer<typeof SaveComplianceDraftResponseSchema>;

// ============================================================================
// GET AVAILABLE PHONE NUMBERS
// ============================================================================

/**
 * Get Available Phone Numbers Query Schema
 */
/** Searchable phone number types — infer from Zod, single source of truth */
export const SEARCHABLE_NUMBER_TYPES = ["mobile", "local", "toll_free"] as const;
export type SearchableNumberType = (typeof SEARCHABLE_NUMBER_TYPES)[number];

export const GetAvailablePhoneNumbersQuerySchema = z.object({
  country: z.string().default("USA"),
  sms: z.string().optional(),
  mms: z.string().optional(),
  voice: z.string().optional(),
  type: z.enum(SEARCHABLE_NUMBER_TYPES).default("local")
});

export type GetAvailablePhoneNumbersQuery = z.infer<typeof GetAvailablePhoneNumbersQuerySchema>;

/**
 * Get Available Phone Numbers Response Schema
 */
export const GetAvailablePhoneNumbersResponseSchema = z.array(PhoneNumberInfoSchema);

export type GetAvailablePhoneNumbersResponse = z.infer<
  typeof GetAvailablePhoneNumbersResponseSchema
>;

// ============================================================================
// BUY PHONE NUMBER
// ============================================================================

/**
 * Buy Phone Number Body Schema
 */
export const BuyPhoneNumberBodySchema = z.object({
  country: z.string(),
  phoneNumber: z.string(),
  price: z.string()
});

export type BuyPhoneNumberBody = z.infer<typeof BuyPhoneNumberBodySchema>;

/**
 * Purchased Phone Number Response Schema
 */
export const BuyPhoneNumberResponseSchema = z.object({
  id: z.string(),
  number: z.string(),
  friendlyName: z.string().nullable(),
  countryCode: z.string(),
  current_price: z.string().nullable(),
  sid: z.string(),
  apiKeyConnectionId: z.string().nullable()
});

export type BuyPhoneNumberResponse = z.infer<typeof BuyPhoneNumberResponseSchema>;

// ============================================================================
// GET MY PHONE NUMBERS
// ============================================================================

/**
 * My Phone Number Schema
 *
 * Full phone number record from database (matches DbEntity.PhoneNumber)
 */
export const MyPhoneNumberSchema = z.object({
  id: z.string(),
  number: z.string(),
  friendlyName: z.string().nullable(),
  countryCode: z.string(),
  current_price: z.string().nullable(),
  sid: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  startingPeriod: z.coerce.date(),
  currentOrganizationId: z.string().nullable(),
  whatsappSenderId: z.string().nullish(), // ✅ Allows null AND undefined (sender can be unassigned)
  apiKeyConnectionId: z.string().nullable(),
  /// Twilio API routing region — source of truth for hostRegion across all deployments on this number.
  /// Twilio API routing region — source of truth for hostRegion across all deployments on this number.
  region: SupportedTwilioRegionSchema
});

export type MyPhoneNumber = z.infer<typeof MyPhoneNumberSchema>;

/**
 * Get My Phone Numbers Response Schema
 */
export const GetMyPhoneNumbersResponseSchema = z.array(MyPhoneNumberSchema);

export type GetMyPhoneNumbersResponse = z.infer<typeof GetMyPhoneNumbersResponseSchema>;

// ============================================================================
// RELEASE PHONE NUMBER (customer-facing soft delete + Twilio release)
// ============================================================================

/** Path var: the DB PhoneNumber id. */
export const PhoneNumberIdPathVarsSchema = z.object({
  phoneNumberId: z.string().uuid()
});

/**
 * The number's lifecycle status, DERIVED FROM THE COLUMN rather than listed here.
 *
 * 🚨 THIS WAS A HAND-WRITTEN THREE AGAINST A FIVE-MEMBER COLUMN, AND THE TWO IT
 * OMITTED ARE THE TWO A RELEASE PASSES THROUGH. `PhoneNumberStatus` holds
 * ACTIVE, PENDING_RELEASE, RELEASING, RELEASED and DELETED; this listed ACTIVE,
 * RELEASED and DELETED. Both responses below carry
 * `$Enums.PhoneNumberStatus` straight from the row, so the two missing members
 * can reach the wire.
 *
 * That is not a cosmetic gap: the generated client `.parse()`s every response
 * with a THROWING parse, so a number sitting in the cooling window made the
 * release dialog and the release result throw rather than render. Widening makes
 * the published type true; it does not change what the server sends.
 *
 * Derived from `PHONE_NUMBER_STATUS_VALUES` so the schema cannot fall behind the
 * column again — the same cure `CUSTOM_MODEL_PROTOCOLS` got when its column
 * became an enum.
 */
const PhoneNumberStatusSchema = z.enum(DbEnum.PHONE_NUMBER_STATUS_VALUES);

/** A deployment attached to the number (shown in the release dialog). */
export const AttachedDeploymentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** DeploymentType value (e.g. PHONE_CALL, TWILIO, WHATSAPP) — display only. */
  type: z.string()
});

/** What is currently attached to a number — drives the release dialog. */
export const PhoneNumberUsageResponseSchema = z.object({
  phoneNumberId: z.string().uuid(),
  number: z.string(),
  status: PhoneNumberStatusSchema,
  deployments: z.array(AttachedDeploymentSchema),
  whatsappSenders: z.array(z.object({ id: z.string(), name: z.string() })),
  releasable: z.boolean()
});

export type PhoneNumberUsageResponse = z.infer<typeof PhoneNumberUsageResponseSchema>;

/** Result of releasing a number. */
export const ReleaseMyPhoneNumberResponseSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  status: PhoneNumberStatusSchema,
  releasedAt: z.string().nullable(),
  releasedBy: z.string().nullable(),
  detachedDeployments: z.number(),
  removedWhatsappSenders: z.number(),
  /** False when Twilio rejected the release — number removed in Nexus, admin issue filed. */
  twilioReleased: z.boolean()
});

export type ReleaseMyPhoneNumberResponse = z.infer<typeof ReleaseMyPhoneNumberResponseSchema>;

// ============================================================================
// TRANSFER PHONE NUMBER
// ============================================================================

/**
 * Transfer Phone Number Body Schema
 */
export const TransferPhoneNumberBodySchema = z.object({
  phoneNumber: z.string(),
  phoneNumberSid: z.string(),
  subAccountId: z.string(),
  // No `organizationId` — the tenant comes from the caller's own claim via
  // `@CurrentOrganizationId()`. It used to be a body field on a route that also
  // carried `@AllowUnauthenticated()`, so an anonymous caller named the tenant
  // it wrote into and the only check was that the id agreed with the
  // `subAccountId` beside it (NEX-3141).
  transferPhoneNumber: z.boolean(),
  countryCode: z.string(),
  bundleSid: z.string(),
  addressSid: z.string()
});

export type TransferPhoneNumberBody = z.infer<typeof TransferPhoneNumberBodySchema>;

/**
 * Transfer Phone Number Response Schema
 * Note: The service returns { success: true } after transfer
 */
export const TransferPhoneNumberResponseSchema = z.object({
  success: z.boolean()
});

export type TransferPhoneNumberResponse = z.infer<typeof TransferPhoneNumberResponseSchema>;

// ============================================================================
// TWILIO ADDRESSES (compliance wizard address picker)
// ============================================================================

/**
 * Twilio Address Schema
 *
 * Represents a Twilio Address resource used for regulatory compliance.
 * Addresses belong to a sub-account and are linked to compliance bundles.
 */
export const TwilioAddressSchema = z.object({
  sid: z.string(),
  customerName: z.string(),
  street: z.string(),
  city: z.string(),
  region: z.string(),
  postalCode: z.string(),
  isoCountry: z.string(),
  friendlyName: z.string().nullable()
});

export type TwilioAddress = z.infer<typeof TwilioAddressSchema>;

/**
 * List Addresses Query Schema
 *
 * Query params for listing addresses on a Twilio sub-account.
 * connectionId identifies which ApiKeyConnection (sub-account) to query.
 */
export const ListAddressesQuerySchema = z.object({
  connectionId: z.string().uuid()
});

export type ListAddressesQuery = z.infer<typeof ListAddressesQuerySchema>;

/**
 * Create Address Body Schema
 *
 * Body for creating a new Twilio Address on a sub-account.
 * All fields are required by Twilio's Address API.
 */
export const CreateAddressBodySchema = z.object({
  connectionId: z.string().uuid(),
  customerName: z.string().min(1),
  friendlyName: z.string().trim().min(1),
  street: z.string().min(1),
  streetSecondary: z.string().optional(),
  city: z.string().min(1),
  region: z.string().min(1),
  postalCode: z.string().min(1),
  isoCountry: z.string().length(2)
});

export type CreateAddressBody = z.infer<typeof CreateAddressBodySchema>;

export const DeleteAddressBodySchema = z.object({
  connectionId: z.string().uuid(),
  addressSid: z.string().min(1)
});

export type DeleteAddressBody = z.infer<typeof DeleteAddressBodySchema>;

// ============================================================================
// SUBMIT COMPLIANCE BUNDLE
// ============================================================================

/**
 * A document submitted as part of a compliance bundle.
 * Each entry corresponds to one regulation document requirement group.
 */
export const ComplianceDocumentSubmissionSchema = z.object({
  /** Twilio's stable identifier for the AND requirement this entry satisfies
   *  (e.g. "business_name_info"). Refetch-stable — survives any reorder of
   *  the regulation's `documentRequirements` between the wizard's load and
   *  submit. */
  requirementName: z.string().min(1),
  /** Twilio document type (e.g. "government_issued_document_image") */
  documentType: z.string().min(1),
  /** Human-readable label for the document */
  friendlyName: z.string().trim().min(1),
  /** Document-specific metadata fields (machineName -> value) */
  fields: z.record(z.string(), z.string()).default({}),
  /** Whether this document requirement needs a Twilio Address linked */
  requiresAddress: z.boolean().default(false),
  /** File content as base64 (for document types that require file upload) */
  fileBase64: z.string().optional(),
  /** Original file name */
  fileName: z.string().optional(),
  /** MIME type of the uploaded file */
  fileMimeType: z.string().optional()
});

export type ComplianceDocumentSubmission = z.infer<typeof ComplianceDocumentSubmissionSchema>;

/**
 * Submit Compliance Bundle Body Schema
 *
 * Creates a Twilio regulatory compliance bundle from the wizard form data:
 * 1. Creates Bundle with regulationSid
 * 2. Creates EndUser with end-user fields
 * 3. Creates SupportingDocuments for each document requirement
 * 4. Assigns all items to the bundle
 * 5. Submits bundle for Twilio review
 */
export const SubmitComplianceBundleBodySchema = z.object({
  connectionId: z.string().uuid(),
  regulationSid: z.string().min(1),
  isoCountry: z.string().length(2),
  numberType: z.string().min(1),
  endUserType: z.enum(["individual", "business"]),

  /** End-user fields from the regulation (machineName -> value) */
  endUserFields: z.record(z.string(), z.string()),

  /** Documents to submit — one per requirement group */
  documents: z.array(ComplianceDocumentSubmissionSchema),

  /** Twilio Address SID (for regulations that require address proof) */
  addressSid: z.string().optional()
});

export type SubmitComplianceBundleBody = z.infer<typeof SubmitComplianceBundleBodySchema>;

/**
 * Submit Compliance Bundle Response Schema
 *
 * Returns the created bundle details after successful submission.
 */
export const SubmitComplianceBundleResponseSchema = z.object({
  bundleSid: z.string(),
  status: z.string(),
  endUserSid: z.string(),
  documentSids: z.array(z.string())
});

export type SubmitComplianceBundleResponse = z.infer<typeof SubmitComplianceBundleResponseSchema>;

// ============================================================================
// SYNC COMPLIANCE STATUS (webhook fallback)
// ============================================================================

export const SyncComplianceStatusBodySchema = z.object({
  connectionId: z.string().uuid()
});

export type SyncComplianceStatusBody = z.infer<typeof SyncComplianceStatusBodySchema>;

export const SyncComplianceStatusResponseSchema = z.object({
  updated: z.boolean(),
  status: z.string()
});

export type SyncComplianceStatusResponse = z.infer<typeof SyncComplianceStatusResponseSchema>;

// ============================================================================
// A2P 10DLC REGISTRATION (submit initial registration)
// ============================================================================

/**
 * Submit A2P Registration Body Schema
 *
 * Creates a Twilio Customer Profile + Trust Product for A2P 10DLC
 * messaging registration. Both are submitted for Twilio review.
 * Brand registration happens after async approval (separate endpoint).
 */
export const SubmitA2pRegistrationBodySchema = z.object({
  connectionId: z.string().uuid(),

  // Business identity
  businessName: z.string().min(1),
  ein: z.string().min(1),
  registrationIdentifier: z.enum(A2P_REGISTRATION_IDENTIFIERS).default("EIN"),
  businessType: z.enum(A2P_BUSINESS_TYPES),
  businessIndustry: z.enum(A2P_BUSINESS_INDUSTRIES),
  companyType: z.enum(A2P_COMPANY_TYPES),

  // Online presence
  websiteUrl: z.string().url(),
  socialMediaUrl: z.string().url().optional(),

  // Contact
  brandContactEmail: z.string().email(),
  businessRegionsOfOperation: z.string().default("USA"),

  // Authorized representative
  authorizedRep: A2pAuthorizedRepSchema,

  // Address
  address: A2pBusinessAddressSchema
});

export type SubmitA2pRegistrationBody = z.infer<typeof SubmitA2pRegistrationBodySchema>;

/**
 * Submit A2P Registration Response Schema
 *
 * Returns the created Trust Hub resource SIDs and overall status.
 * Status will be "pending_brand" — waiting for Twilio to approve
 * the Customer Profile and Trust Product before brand registration.
 */
export const SubmitA2pRegistrationResponseSchema = z.object({
  customerProfileSid: z.string(),
  trustProductSid: z.string(),
  status: z.string()
});

export type SubmitA2pRegistrationResponse = z.infer<typeof SubmitA2pRegistrationResponseSchema>;

// ============================================================================
// SYNC A2P STATUS (brand + campaign polling fallback)
// ============================================================================

export const SyncA2pStatusBodySchema = z.object({
  connectionId: z.string().uuid()
});

export type SyncA2pStatusBody = z.infer<typeof SyncA2pStatusBodySchema>;

export const SyncA2pStatusResponseSchema = z.object({
  updated: z.boolean(),
  status: z.string()
});

export type SyncA2pStatusResponse = z.infer<typeof SyncA2pStatusResponseSchema>;
